import { db } from '@/models/db';
import type { DeltaPacketRecord } from '@/models/hydrate.ts';
import { AppraisalV2 } from '@/models/registry.ts';
import type { BaseStore } from '@/models/stores/base.ts';
import type { AppraisalV2 as AppraisalV2Type } from '@/models/types.ts';
import {
  computed,
  makeAutoObservable,
  observable,
  type ObservableMap,
} from 'mobx';

class AppraisalStoreV2 implements BaseStore<AppraisalV2, AppraisalV2Type> {
  store: ObservableMap<string, AppraisalV2> = observable.map();

  storeIndexedByForeignKey: Map<string, AppraisalV2[]> = new Map();

  constructor() {
    makeAutoObservable(
      this,
      {
        store: observable.shallow,
      },
      { autoBind: true },
    );
  }

  private addToDealIndex(appraisal: AppraisalV2) {
    if (!appraisal.deal_id) return;
    const arr = this.storeIndexedByForeignKey.get(appraisal.deal_id) || [];
    if (arr.findIndex((a) => a.id === appraisal.id) === -1) {
      arr.push(appraisal);
    }
    this.storeIndexedByForeignKey.set(appraisal.deal_id, arr);
  }

  private removeFromDealIndex(appraisal: AppraisalV2) {
    if (!appraisal.deal_id) return;
    const arr = this.storeIndexedByForeignKey.get(appraisal.deal_id);
    if (!arr) return;
    const filtered = arr.filter((a) => a.id !== appraisal.id);
    if (filtered.length === 0) {
      this.storeIndexedByForeignKey.delete(appraisal.deal_id);
    } else {
      this.storeIndexedByForeignKey.set(appraisal.deal_id, filtered);
    }
  }

  private setAppraisalField = <K extends keyof AppraisalV2>(
    appraisal: AppraisalV2,
    key: K,
    value: AppraisalV2[K],
  ) => {
    (appraisal as Pick<AppraisalV2, K>)[key] = value as AppraisalV2[K];
  };

  applyDeltaV2({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<AppraisalV2Type>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete' || delta.action === 'archive') {
      if (hydrateIndexedDB) {
        db.appraisals_v2.delete(delta.record_id);
      }
      this.remove(delta.record_id);
      return;
    }

    if (delta.action === 'create') {
      if (hydrateIndexedDB) {
        db.appraisals_v2.put(delta.data, delta.record_id);
      }
      this.upsert(delta.data);
      return;
    }

    if (delta.action === 'update') {
      if (hydrateIndexedDB) {
        db.appraisals_v2.update(delta.record_id, delta.data);
      }
      this.replaceOnlyUpdatedFields({ delta });
      return;
    }

    if (hydrateIndexedDB) {
      db.appraisals_v2.put(delta.data, delta.record_id);
    }
    this.upsert(delta.data);
  }

  replaceOnlyUpdatedFields({
    delta,
  }: {
    delta: DeltaPacketRecord<AppraisalV2Type>;
  }) {
    const existingAppraisal = this.getById(delta.record_id);
    if (!existingAppraisal) return;

    // Keep the deal_id index in sync when a partial update changes the link.
    if (
      'deal_id' in delta.data &&
      delta.data.deal_id !== existingAppraisal.deal_id
    ) {
      this.removeFromDealIndex(existingAppraisal);
      existingAppraisal.deal_id = delta.data.deal_id;
      this.addToDealIndex(existingAppraisal);
    }

    const entries = Object.entries(delta.data) as [
      keyof AppraisalV2Type,
      AppraisalV2Type[keyof AppraisalV2Type],
    ][];

    for (const [key, newValue] of entries) {
      if (
        key === 'deal_id' ||
        newValue === undefined ||
        existingAppraisal[key] === newValue
      ) {
        continue;
      }

      this.setAppraisalField(existingAppraisal, key, newValue);
    }
  }

  applyDeltas({
    deltas,
    hydrateIndexedDB,
  }: {
    deltas: DeltaPacketRecord<AppraisalV2Type>[];
    hydrateIndexedDB?: boolean;
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta, hydrateIndexedDB });
    }
  }

  applyDelta({
    delta,
    hydrateIndexedDB,
  }: {
    delta: DeltaPacketRecord<AppraisalV2Type>;
    hydrateIndexedDB?: boolean;
  }) {
    if (delta.action === 'delete') {
      this.remove(delta.record_id);
      return;
    }

    if (hydrateIndexedDB) {
      db.appraisals_v2.put(delta.data, delta.record_id);
    }

    this.upsert(delta.data);
  }

  getById(id: string): AppraisalV2 | undefined {
    return this.store.get(id);
  }

  get all(): AppraisalV2[] {
    return Array.from(this.store.values());
  }

  @computed.struct
  get allByMostRecent(): readonly AppraisalV2[] {
    // Copy before sorting: `this.all` is a cached MobX computed, and `.sort()`
    // mutates in place. Sorting it directly would mutate (and, via the freeze
    // below, freeze) the shared cached array, causing
    // "Cannot assign to read only property '0'" on a later re-sort.
    const sorted = [...this.all].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return String(b.id).localeCompare(String(a.id));
    });

    return Object.freeze(sorted);
  }

  add(appraisal: AppraisalV2Type) {
    const newAppraisal = new AppraisalV2(appraisal);
    this.store.set(newAppraisal.id, newAppraisal);
    this.addToDealIndex(newAppraisal);
  }

  upsert(appraisal: AppraisalV2Type) {
    const idToUse = appraisal.id; // No String() casting needed for UUID
    const existingAppraisal = this.getById(idToUse);
    if (existingAppraisal) {
      this.replace({ id: idToUse, appraisal });
    } else {
      this.add(appraisal);
    }
  }

  replace({ id, appraisal }: { id: string; appraisal: AppraisalV2Type }) {
    const existingAppraisal = this.getById(id);
    if (!existingAppraisal) return;

    if (existingAppraisal.deal_id !== appraisal.deal_id) {
      this.removeFromDealIndex(existingAppraisal);
      existingAppraisal.deal_id = appraisal.deal_id;
      this.addToDealIndex(existingAppraisal);
    }

    existingAppraisal.tracking_id = appraisal.tracking_id;
    existingAppraisal.vin = appraisal.vin;
    existingAppraisal.make = appraisal.make;
    existingAppraisal.model = appraisal.model;
    existingAppraisal.year = appraisal.year;
    existingAppraisal.zip_code = appraisal.zip_code;
    existingAppraisal.phone_number = appraisal.phone_number;
    existingAppraisal.drivetrain = appraisal.drivetrain;
    existingAppraisal.trim = appraisal.trim;
    existingAppraisal.body = appraisal.body;
    existingAppraisal.mileage = appraisal.mileage;
    existingAppraisal.exterior_color = appraisal.exterior_color;
    existingAppraisal.interior_color = appraisal.interior_color;
    existingAppraisal.seating_capacity = appraisal.seating_capacity;
    existingAppraisal.num_of_keys = appraisal.num_of_keys;
    existingAppraisal.condition = appraisal.condition;
    existingAppraisal.num_of_accidents = appraisal.num_of_accidents;
    existingAppraisal.accident_type = appraisal.accident_type;
    existingAppraisal.exterior_damage_types = appraisal.exterior_damage_types;
    existingAppraisal.exterior_damage_description =
      appraisal.exterior_damage_description;
    existingAppraisal.interior_damage_types = appraisal.interior_damage_types;
    existingAppraisal.interior_damage_description =
      appraisal.interior_damage_description;
    existingAppraisal.mechanical_damage_types =
      appraisal.mechanical_damage_types;
    existingAppraisal.mechanical_damage_description =
      appraisal.mechanical_damage_description;
    existingAppraisal.self_driving_capability =
      appraisal.self_driving_capability;
    existingAppraisal.highlights = appraisal.highlights;
    existingAppraisal.status = appraisal.status;
    existingAppraisal.is_upgrade_or_purchase_interested =
      appraisal.is_upgrade_or_purchase_interested;
    existingAppraisal.upgrade_or_purchase_interested_info =
      appraisal.upgrade_or_purchase_interested_info;
    existingAppraisal.is_serviced_regularly = appraisal.is_serviced_regularly;
    existingAppraisal.is_contacted_by_sales = appraisal.is_contacted_by_sales;
    existingAppraisal.created_at = appraisal.created_at;
    existingAppraisal.updated_at = appraisal.updated_at;

    this.store.set(id, existingAppraisal);
  }

  remove(id: AppraisalV2Type['id']) {
    const idToUse = id; // No String() casting needed for UUID
    const appraisal = this.getById(idToUse);
    if (!appraisal) return;
    this.removeFromDealIndex(appraisal);
    this.store.delete(idToUse);
    db.appraisals_v2.delete(idToUse);
  }
}

export const appraisalStoreV2 = new AppraisalStoreV2();
